import { useState, useRef, useCallback } from 'react'
import { searchAirports } from '../config/api'

const DEBOUNCE_MS = 300
const MIN_QUERY_LENGTH = 2

const isCityAirportCode = (airport = {}) =>
  !airport.icao || /all airports/i.test(airport.name || '')

const airportSearchCodeType = (airport = {}) =>
  isCityAirportCode(airport) ? 'city' : 'airport'

/**
 * Manages airport autocomplete for a single field (From or To).
 * Handles debouncing, per-session caching, and in-flight abort.
 *
 * Returns:
 *   query          — current text in the input
 *   setQuery       — call on every keystroke; triggers debounced search
 *   iata           — IATA code of the selected airport (empty until selection)
 *   suggestions    — flat array of airport objects from LiteAPI
 *   loading        — true while a request is in flight
 *   showSuggestions
 *   setShowSuggestions
 *   handleSelect(airport) — call when the user picks a result
 *   clear()        — reset everything (e.g. when switching search type)
 */
export function useAirportSearch() {
  const [query, setQueryRaw] = useState('')
  const [iata, setIata] = useState('')
  const [selectedAirport, setSelectedAirport] = useState(null)
  const [lastSelectedAirport, setLastSelectedAirport] = useState(null)
  const [suggestions, setSuggestions] = useState([])
  const [loading, setLoading] = useState(false)
  const [showSuggestions, setShowSuggestions] = useState(false)

  const cache = useRef({})
  const debounceTimer = useRef(null)
  const abortRef = useRef(null)
  const latestQuery = useRef('')

  const runSearch = useCallback(async (q) => {
    const cacheKey = q.toLowerCase().trim()

    if (cache.current[cacheKey]) {
      setSuggestions(cache.current[cacheKey])
      setShowSuggestions(cache.current[cacheKey].length > 0)
      setLoading(false)
      return
    }

    if (abortRef.current) abortRef.current.abort()
    abortRef.current = new AbortController()

    setLoading(true)
    try {
      const results = (await searchAirports(q, abortRef.current.signal)).map(airport => ({
        ...airport,
        codeType: airportSearchCodeType(airport),
        isCityCode: isCityAirportCode(airport),
      }))
      // Discard if a newer query has already fired
      if (q !== latestQuery.current) return
      cache.current[cacheKey] = results
      setSuggestions(results)
      setShowSuggestions(results.length > 0)
    } catch (e) {
      if (e.name !== 'AbortError') console.warn('[useAirportSearch]', e)
    } finally {
      setLoading(false)
    }
  }, [])

  const setQuery = useCallback((value) => {
    setQueryRaw(value)
    setIata('')
    setSelectedAirport(null)

    if (debounceTimer.current) clearTimeout(debounceTimer.current)

    if (!value || value.length < MIN_QUERY_LENGTH) {
      setSuggestions([])
      setShowSuggestions(false)
      return
    }

    latestQuery.current = value
    debounceTimer.current = setTimeout(() => runSearch(value), DEBOUNCE_MS)
  }, [runSearch])

  const handleSelect = useCallback((airport) => {
    const display = airport.city
      ? `${airport.city} (${airport.iata})${isCityAirportCode(airport) ? ' - all airports' : ''}`
      : airport.name
    const hydrated = {
      ...airport,
      codeType: airport.codeType || airportSearchCodeType(airport),
      isCityCode: airport.isCityCode ?? isCityAirportCode(airport),
    }
    setQueryRaw(display)
    setIata(hydrated.iata)
    setSelectedAirport(hydrated)
    setLastSelectedAirport(hydrated)
    setSuggestions([])
    setShowSuggestions(false)
  }, [])

  const setSelection = useCallback((airport) => {
    if (!airport) return
    const display = airport.query || (airport.city
      ? `${airport.city} (${airport.iata})${isCityAirportCode(airport) ? ' - all airports' : ''}`
      : airport.name || airport.iata)
    const hydrated = {
      ...airport,
      codeType: airport.codeType || airportSearchCodeType(airport),
      isCityCode: airport.isCityCode ?? isCityAirportCode(airport),
    }
    setQueryRaw(display)
    setIata(hydrated.iata || '')
    setSelectedAirport(hydrated)
    setLastSelectedAirport(hydrated)
    setSuggestions([])
    setShowSuggestions(false)
  }, [])

  const focusForEdit = useCallback(() => {
    const remembered = selectedAirport || lastSelectedAirport
    if (remembered) {
      setQueryRaw('')
      setIata('')
      setSelectedAirport(null)
      setSuggestions([remembered])
      setShowSuggestions(true)
      if (debounceTimer.current) clearTimeout(debounceTimer.current)
      if (abortRef.current) abortRef.current.abort()
      return
    }
    if (suggestions.length > 0) setShowSuggestions(true)
  }, [selectedAirport, lastSelectedAirport, suggestions])

  const clear = useCallback(() => {
    setQueryRaw('')
    setIata('')
    setSelectedAirport(null)
    setLastSelectedAirport(null)
    setSuggestions([])
    setShowSuggestions(false)
    if (debounceTimer.current) clearTimeout(debounceTimer.current)
    if (abortRef.current) abortRef.current.abort()
  }, [])

  return {
    query,
    setQuery,
    iata,
    selectedAirport,
    suggestions,
    loading,
    showSuggestions,
    setShowSuggestions,
    handleSelect,
    setSelection,
    focusForEdit,
    clear,
  }
}
